/**
 * ป้ายระบุ "ชิ้นของ" ในใบตามเก็บอุปกรณ์ — helper กลาง ที่เดียวของทั้งระบบ
 *
 * ที่มา (feedback ทิวากร ทาง LINE):
 *   "ถ้าได้รับเครื่องหลัก แต่ซิมหาย ต้องกดยังไง"
 *   "มันก็ไม่ได้แยกให้เห็นชัด ๆ ว่าซิมหายอย่างเดียว"
 *
 * เดิม 1 แถวของ `equipment_return_items` ถือทั้งเครื่องและซิม (device_id + sim_id ในแถวเดียว
 * มี status เดียว equipment_condition เดียว) → บันทึก "เครื่องกลับ ซิมหาย" ไม่ได้เลย
 * และทุกจอเขียนสูตรป้ายชื่อของตัวเอง โดยให้ซิมเป็น **ตัวสำรอง** ของเครื่องเสมอ เช่น
 *   EquipmentReturnDashboard.tsx:127
 *     item.device?.device_code || item.device?.code || item.sim?.sim_number || `#${item.id}`
 * ซึ่งอ่านว่า "ถ้าไม่มีเครื่องค่อยดูซิม" = ซิมถูกลดชั้นเป็นข้อมูลประกอบ ไม่ใช่ของอีกชิ้น
 *
 * ตั้งแต่รอบนี้ backend แยกเป็น **1 แถว = ของ 1 ชิ้น** (แถวเครื่อง sim=null / แถวซิม device=null)
 * ไฟล์นี้คือกติกาเดียวที่แปลง 1 แถว → ป้ายไทยที่อ่านปราดเดียวรู้ว่าเป็นของชิ้นไหน
 *
 * 🚫 ห้ามให้จอไหนเขียนสูตรป้ายชื่อเอง — เพิ่มที่นี่ที่เดียวแล้วทุกจอได้พร้อมกัน
 */

/** ประเภทของ "ชิ้นของ" ที่แถวหนึ่งถืออยู่ */
export type ReturnItemKind = 'DEVICE' | 'SIM' | 'COMBINED' | 'UNKNOWN';

/**
 * รูปร่างขั้นต่ำที่ helper ต้องการ — จงใจ duck-type ไม่ผูกกับ interface ใดที่เดียว
 * เพราะแต่ละ service/types ในโปรเจกต์ประกาศชื่อฟิลด์ไม่ตรงกัน
 * (device.device_code vs device.code · sim.carrier vs sim.operator) ซึ่งเป็นต้นเหตุ
 * ที่แต่ละจอเขียนสูตร fallback ของตัวเองมาแต่แรก
 */
export interface ReturnItemLike {
  id?: number;
  device_id?: number | null;
  sim_id?: number | null;
  rental_item_id?: number | null;
  status?: string | null;
  device?: {
    id?: number;
    device_code?: string | null;
    /** บาง endpoint คืน `code` แทน `device_code` */
    code?: string | null;
    serial_number?: string | null;
    model_name?: string | null;
    model?: string | null;
  } | null;
  sim?: {
    id?: number;
    sim_number?: string | null;
    phone_number?: string | null;
    carrier?: string | null;
    /** ManualReturnRequestModal / rentalService ใช้ชื่อ `operator` */
    operator?: string | null;
  } | null;
}

/** ผลลัพธ์ที่จอเอาไปวาดได้ตรง ๆ โดยไม่ต้องตัดสินใจอะไรเพิ่ม */
export interface ReturnItemDescriptor {
  kind: ReturnItemKind;
  /** ป้ายประเภทสั้น ๆ สำหรับ badge — 'เครื่อง' / 'ซิม' / 'เครื่อง + ซิม' */
  kindLabel: string;
  /** บรรทัดหลัก — ตัวที่ผู้ใช้ใช้เรียกของชิ้นนี้จริง ๆ */
  primary: string;
  /** บรรทัดรอง (S/N ของเครื่อง · เลขซิม) — undefined = ไม่มีข้อมูล ห้ามวาดบรรทัดว่าง */
  secondary?: string;
  /** บรรทัดที่สาม (รุ่นเครื่อง · ค่ายซิม) */
  tertiary?: string;
  /** แถวข้อมูลเก่าที่ยังถือทั้งเครื่องและซิม (ไม่ถูก migrate เพราะปิดไปแล้ว) */
  isLegacyCombined: boolean;
  /** ป้ายเดียวจบสำหรับปุ่ม/กล่องยืนยัน — "เครื่อง DEV-12 (S/N 123)" */
  shortLabel: string;
}

const KIND_LABEL: Record<ReturnItemKind, string> = {
  DEVICE: 'เครื่อง',
  SIM: 'ซิม',
  COMBINED: 'รายการรวม (รูปแบบเดิม)',
  UNKNOWN: 'ไม่ระบุประเภท',
};

/**
 * คำอธิบายแถวรูปเก่า — ใช้เป็น tooltip ทุกที่ที่แสดงแถวแบบนี้
 *
 * แถวรูปเก่าจะ **ยังเหลืออยู่จริงบน production** ไม่ใช่เคสสมมติ:
 * migration แยกเฉพาะแถวที่ยังเปิดอยู่ (prod 36 แถว) ส่วนแถวที่ปิดไปแล้ว (prod 10 แถว)
 * คงเป็นแถวรวมตามเดิมโดยตั้งใจ — เพราะของถูกปิดพร้อมกัน วันเดียวกัน สภาพเดียวกัน
 * การแยกประวัติย้อนหลังจึงเป็นการแต่งเรื่อง
 */
export const LEGACY_COMBINED_HINT =
  'รายการรวมรูปแบบเดิม — แถวเดียวถือทั้งเครื่องและซิม เพราะถูกปิดไปก่อนวันปรับปรุงระบบ ' +
  '(ของถูกรับคืนพร้อมกัน สภาพเดียวกัน) จึงบันทึกสภาพแยกชิ้นย้อนหลังไม่ได้ ' +
  'ถ้าต้องแก้ไข ให้กด "เปิดรายการใหม่" ระบบจะแยกเป็นเครื่อง 1 แถวและซิม 1 แถวให้';

/** ค่าที่ถือว่า "ไม่มีข้อมูล" — ข้อมูลจริงบน prod มีทั้ง null, '', '-', 'null' */
const isBlank = (v: unknown): boolean => {
  if (v === null || v === undefined) return true;
  const s = String(v).trim();
  return s === '' || s === '-' || s.toLowerCase() === 'null' || s.toLowerCase() === 'undefined';
};

const clean = (v: unknown): string | undefined => (isBlank(v) ? undefined : String(v).trim());

/** แถวนี้ถือเครื่องอยู่ไหม — ต้องดู `device_id` ด้วย เพราะ list endpoint บาง path ไม่ expand `device` */
export const hasDevicePart = (item: ReturnItemLike): boolean =>
  Boolean(item.device) || (item.device_id !== null && item.device_id !== undefined);

/** แถวนี้ถือซิมอยู่ไหม — เหตุผลเดียวกับ hasDevicePart */
export const hasSimPart = (item: ReturnItemLike): boolean =>
  Boolean(item.sim) || (item.sim_id !== null && item.sim_id !== undefined);

export function returnItemKind(item: ReturnItemLike): ReturnItemKind {
  const dev = hasDevicePart(item);
  const sim = hasSimPart(item);
  if (dev && sim) return 'COMBINED';
  if (dev) return 'DEVICE';
  if (sim) return 'SIM';
  return 'UNKNOWN';
}

const deviceCodeOf = (item: ReturnItemLike): string | undefined =>
  clean(item.device?.device_code) ?? clean(item.device?.code);

const simCarrierOf = (item: ReturnItemLike): string | undefined =>
  clean(item.sim?.carrier) ?? clean(item.sim?.operator);

/**
 * แปลง 1 แถว → ป้ายที่พร้อมวาด
 *
 * กติกาที่ตั้งใจ:
 * - เครื่อง: บรรทัดหลัก = **รหัสเครื่อง** (สิ่งที่คนพูดถึงกันจริง) · S/N ลงบรรทัดรอง และ
 *   แสดงเฉพาะเมื่อมีจริง — ของนับจำนวนไม่มี S/N (เหตุผลเดียวกับ utils/deviceOptionLabel)
 * - ซิม: บรรทัดหลัก = **เบอร์โทร** (สิ่งที่คนใช้เรียกซิม) · เลขซิมลงบรรทัดรอง และ
 *   **ตัดทิ้งเมื่อซ้ำกับเบอร์** ไม่งั้นได้ "0812345678 (0812345678)" แบบที่
 *   ReturnItemFollowUpModal ทำอยู่เดิม
 * - ข้อมูลไม่ครบ (มี id แต่ไม่ expand) → "อุปกรณ์ #12" ไม่ใช่ช่องว่าง
 */
export function describeReturnItem(item: ReturnItemLike): ReturnItemDescriptor {
  const kind = returnItemKind(item);

  const devCode = deviceCodeOf(item);
  const devSerial = clean(item.device?.serial_number);
  const devModel = clean(item.device?.model_name) ?? clean(item.device?.model);
  const devFallbackId = item.device?.id ?? item.device_id;

  const simPhone = clean(item.sim?.phone_number);
  const simNumber = clean(item.sim?.sim_number);
  const simCarrier = simCarrierOf(item);
  const simFallbackId = item.sim?.id ?? item.sim_id;

  const devicePrimary =
    devCode ?? devSerial ?? (devFallbackId != null ? `อุปกรณ์ #${devFallbackId}` : 'อุปกรณ์');
  // เลขซิมใช้เป็นบรรทัดหลักได้ถ้าไม่มีเบอร์ — แต่ห้ามซ้ำกันสองบรรทัด
  const simPrimary =
    simPhone ?? simNumber ?? (simFallbackId != null ? `ซิม #${simFallbackId}` : 'ซิม');
  const simSecondary = simNumber && simNumber !== simPrimary ? `เลขซิม ${simNumber}` : undefined;

  if (kind === 'DEVICE') {
    return {
      kind,
      kindLabel: KIND_LABEL.DEVICE,
      primary: devicePrimary,
      secondary: devSerial && devSerial !== devicePrimary ? `S/N ${devSerial}` : undefined,
      tertiary: devModel,
      isLegacyCombined: false,
      shortLabel: buildShort(KIND_LABEL.DEVICE, devicePrimary, devSerial && devSerial !== devicePrimary ? `S/N ${devSerial}` : undefined),
    };
  }

  if (kind === 'SIM') {
    return {
      kind,
      kindLabel: KIND_LABEL.SIM,
      primary: simPrimary,
      secondary: simSecondary,
      tertiary: simCarrier ? `ค่าย ${simCarrier}` : undefined,
      isLegacyCombined: false,
      shortLabel: buildShort(KIND_LABEL.SIM, simPrimary, simCarrier ? `ค่าย ${simCarrier}` : undefined),
    };
  }

  if (kind === 'COMBINED') {
    // แถวรูปเก่า (ปิดก่อนวันปรับปรุง) — ยังมีจริงบน prod ต้อง "รองรับ" ไม่ใช่แค่ "ไม่พัง":
    // ผู้ใช้ต้องอ่านออกว่าเป็นของ 2 ชิ้นในแถวเดียว และรู้ว่าทำไมกดแยกสภาพไม่ได้
    return {
      kind,
      kindLabel: KIND_LABEL.COMBINED,
      primary: `${devicePrimary} + ซิม ${simPrimary}`,
      secondary: devSerial && devSerial !== devicePrimary ? `S/N ${devSerial}` : undefined,
      tertiary: devModel,
      isLegacyCombined: true,
      shortLabel: `รายการรวม ${devicePrimary} + ซิม ${simPrimary}`,
    };
  }

  const idText = item.id != null ? `รายการ #${item.id}` : 'รายการคืนอุปกรณ์';
  return {
    kind: 'UNKNOWN',
    kindLabel: KIND_LABEL.UNKNOWN,
    primary: idText,
    secondary: undefined,
    tertiary: undefined,
    isLegacyCombined: false,
    shortLabel: idText,
  };
}

function buildShort(kindLabel: string, primary: string, extra?: string): string {
  return extra ? `${kindLabel} ${primary} (${extra})` : `${kindLabel} ${primary}`;
}

/** ป้ายบรรทัดเดียวสำหรับปุ่ม / กล่องยืนยัน / ข้อความ alert */
export const returnItemShortLabel = (item: ReturnItemLike): string =>
  describeReturnItem(item).shortLabel;

// ============================================================
// สถานะแบบย่อ — ใช้สรุป "เครื่อง: คืนแล้ว · ซิม: ยังไม่คืน"
// ============================================================

/** สถานะที่ถือว่าปิดแล้ว (ไม่มีอะไรให้ตามต่อ) */
export const CLOSED_RETURN_STATUSES = ['COMPLETED', 'CANCELLED', 'LOST'] as const;

export const isClosedReturnStatus = (status?: string | null): boolean =>
  Boolean(status) && (CLOSED_RETURN_STATUSES as readonly string[]).includes(String(status));

/**
 * คำสั้นที่สุดที่ยังบอกความจริง — ใช้ในสรุปชุด ไม่ใช่แทน badge สถานะเต็ม
 * (badge เต็มยังใช้ RETURN_STATUS_LABELS/service.getStatusLabel เหมือนเดิม)
 */
export function returnItemStatusShort(status?: string | null): string {
  switch (status) {
    case 'COMPLETED':
      return 'คืนแล้ว';
    case 'LOST':
      return 'สูญหาย';
    case 'CANCELLED':
      return 'ยกเลิกรายการ';
    case 'AT_COMPANY_OFFICE':
      // FB-2026-0010: ยังไม่กลับคลัง แต่รู้แล้วว่าอยู่ที่ไหน — บอกให้ชัดในสรุปชุด
      return 'อยู่ที่บริษัท รปภ';
    case 'PENDING':
    case 'SCHEDULED':
    case 'IN_PROGRESS':
      return 'ยังไม่คืน';
    default:
      return 'ไม่ทราบสถานะ';
  }
}

// ============================================================
// การจัดกลุ่ม — 2 แถวที่มาจากรายการเช่าเดียวกันต้องอยู่ติดกัน
// ============================================================

export interface GroupedReturnRow<T extends ReturnItemLike> {
  item: T;
  descriptor: ReturnItemDescriptor;
  /** กุญแจของชุด — แถวที่ไม่มี rental_item_id จะได้กุญแจเดี่ยวของตัวเอง (ห้ามยุบรวมกัน) */
  groupKey: string;
  /** จำนวนแถวในชุด **เท่าที่มองเห็นในหน้านี้** (pagination อาจตัดชุดขาด — ดู note ด้านล่าง) */
  groupSize: number;
  indexInGroup: number;
  isGroupStart: boolean;
  isGroupEnd: boolean;
  /** สรุปสถานะทั้งชุด เช่น "เครื่อง: คืนแล้ว · ซิม: ยังไม่คืน" — undefined เมื่อชุดมีชิ้นเดียว */
  groupStatusSummary?: string;
  /** ชุดนี้มีทั้งชิ้นที่ปิดแล้วและชิ้นที่ยังค้าง = จุดที่ต้องรีบตาม */
  groupHasMixedProgress: boolean;
}

/** ลำดับการอ่านภายในชุด: เครื่องก่อน แล้วซิม (ของรวมแบบเก่าอยู่กลาง) */
const KIND_ORDER: Record<ReturnItemKind, number> = {
  DEVICE: 0,
  COMBINED: 1,
  SIM: 2,
  UNKNOWN: 3,
};

/**
 * จัดแถวให้ชิ้นที่มาจาก `rental_item_id` เดียวกันอยู่ติดกัน + แนบ metadata ให้จอวาดตัวเชื่อม
 *
 * ⚠️ กับดักที่ตั้งใจกัน: แถวที่ `rental_item_id` เป็น NULL **ห้ามจับกลุ่มรวมกัน**
 * (บน prod มีเยอะ เพราะ ManualReturnRequestModal ไม่เคยส่งฟิลด์นี้) ไม่งั้นทั้งหน้า
 * จะกลายเป็น "ชุดเดียวกัน" ชุดยักษ์ชุดเดียว
 *
 * ⚠️ ลำดับชุดยึด "ลำดับที่เจอครั้งแรก" เพื่อไม่รื้อการเรียงที่ backend ส่งมา (หน้านี้แบ่งหน้า)
 */
export function groupReturnItems<T extends ReturnItemLike>(items: T[]): GroupedReturnRow<T>[] {
  const order: string[] = [];
  const buckets = new Map<string, T[]>();

  items.forEach((item, index) => {
    const key =
      item.rental_item_id !== null && item.rental_item_id !== undefined
        ? `ri:${item.rental_item_id}`
        : `solo:${item.id ?? `idx${index}`}`;
    if (!buckets.has(key)) {
      buckets.set(key, []);
      order.push(key);
    }
    buckets.get(key)!.push(item);
  });

  const rows: GroupedReturnRow<T>[] = [];

  order.forEach((key) => {
    const bucket = buckets.get(key)!;
    // stable sort — เครื่องขึ้นก่อนซิมเสมอ เพื่อให้สรุปชุดอ่านเรียงกันทุกที่
    const sorted = bucket
      .map((item, i) => ({ item, i, rank: KIND_ORDER[returnItemKind(item)] }))
      .sort((a, b) => (a.rank === b.rank ? a.i - b.i : a.rank - b.rank))
      .map((entry) => entry.item);

    const size = sorted.length;
    const descriptors = sorted.map(describeReturnItem);

    const anyClosed = sorted.some((it) => isClosedReturnStatus(it.status));
    const anyOpen = sorted.some((it) => !isClosedReturnStatus(it.status));
    const mixed = size > 1 && anyClosed && anyOpen;

    const summary =
      size > 1
        ? sorted
            .map((it, i) => `${descriptors[i].kindLabel}: ${returnItemStatusShort(it.status)}`)
            .join(' · ')
        : undefined;

    sorted.forEach((item, i) => {
      rows.push({
        item,
        descriptor: descriptors[i],
        groupKey: key,
        groupSize: size,
        indexInGroup: i,
        isGroupStart: i === 0,
        isGroupEnd: i === size - 1,
        groupStatusSummary: summary,
        groupHasMixedProgress: mixed,
      });
    });
  });

  return rows;
}
